import styled from 'styled-components';
import tw from 'twin.macro';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  InlineRow,
  Muted,
  Stack,
  Strong,
} from '../../components/ui';
import {
  checklistItems,
  checklistSummary,
  eventLabel,
  eventTime,
  liveEvents,
  type JobTimeline,
} from './jobTimeline';

const PhotoGrid = styled.div`
  ${tw`grid grid-cols-3 gap-2 sm:grid-cols-4`}
  a, img {
    ${tw`block w-full`}
  }
  img {
    ${tw`object-cover`}
    aspect-ratio: 1;
    border-radius: var(--radius-md);
    background: var(--color-bg-secondary);
  }
`;

const EventRow = styled.div`
  ${tw`flex flex-col gap-2 py-3`}
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`;

const ItemRow = styled.div`
  ${tw`flex items-center justify-between gap-3 py-2 text-sm`}
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-primary);
  &:last-child {
    border-bottom: none;
  }
`;

export function PhotoGridView({ urls }: { urls: readonly string[] }) {
  if (!urls.length) return null;
  return (
    <PhotoGrid>
      {urls.map(url => (
        <a key={url} href={url} target="_blank" rel="noreferrer">
          <img src={url} alt="Job photo" loading="lazy" />
        </a>
      ))}
    </PhotoGrid>
  );
}

/** The checklist with progress (W19, W21). */
export function ChecklistCard({
  timeline,
  actions,
}: {
  timeline: JobTimeline;
  actions?: (code: string) => React.ReactNode;
}) {
  const items = checklistItems(timeline.checklist);
  if (!items.length) return null;
  return (
    <Card data-testid="job-checklist">
      <CardHeader title="Checklist" description={checklistSummary(items)} />
      <CardBody>
        {items.map(item => (
          <ItemRow key={item.code}>
            <span>
              {item.done ? '✓ ' : ''}
              {item.label}
              {item.requiresPhoto && (
                <Muted $size="xs">
                  {' '}
                  · {item.photoCount} of {item.photosNeeded} photos
                </Muted>
              )}
            </span>
            <InlineRow>
              <Badge tone={item.done ? 'success' : 'neutral'} size="sm">
                {item.done ? 'Done' : 'To do'}
              </Badge>
              {actions?.(item.code)}
            </InlineRow>
          </ItemRow>
        ))}
      </CardBody>
    </Card>
  );
}

/**
 * The job's timeline, read only (W19, W21, W22): check-ins with their time and
 * a flag when the GPS did not match the site, checklist photos, problems.
 */
export function JobTimelineView({ timeline, zone }: { timeline: JobTimeline; zone: string }) {
  const items = checklistItems(timeline.checklist);
  const events = liveEvents(timeline.events);
  return (
    <Card data-testid="job-timeline">
      <CardHeader
        title="Timeline"
        description={
          timeline.handoverMinutesSoFar != null
            ? `Handover time so far: ${timeline.handoverMinutesSoFar} min`
            : undefined
        }
      />
      <CardBody>
        {events.length === 0 ? (
          <Muted $size="sm">
            Nothing yet. Check-ins and photos show here as the worker sends them.
          </Muted>
        ) : (
          <Stack $gap={4}>
            {events.map(event => (
              <EventRow key={event.id} data-testid={`job-event-${event.type}`}>
                <InlineRow style={{ justifyContent: 'space-between' }}>
                  <Strong>{eventLabel(event, items)}</Strong>
                  <Muted $size="sm">{eventTime(event, zone)}</Muted>
                </InlineRow>
                {event.locationMismatch && (
                  <Badge tone="warning" size="sm">
                    Location did not match the address
                  </Badge>
                )}
                <PhotoGridView urls={event.photoUrls} />
              </EventRow>
            ))}
          </Stack>
        )}
      </CardBody>
    </Card>
  );
}
